/**
 * Karte „Versände der letzten Woche" (Workshop-Lücke N4, Plan 2026-10-29; Till Q7: letzte volle Kalenderwoche, Heat-Ups je
 * Welle × Liste). Zwei Blöcke, nie in einer Spalte gemischt:
 * - Über den Bestell-Link: Direkt-Mailings und Newsletter-Redlinks zum Produkt — Anfos je 1.000 „bisher" gegen den Schnitt
 *   derselben Fläche in den 12 Wochen davor, dort jede Anfo auf dasselbe Alter geschnitten.
 * - Über die Anmeldung: Heat-Ups der Webinare der Woche — Anmeldungen je 1.000 je Liste × Welle gegen dieselbe Liste × Welle
 *   der 8 Webinare davor; Anfos nur je Webinar „bisher" gegen den Schnitt im selben Alter.
 * Euro nur als „vorläufig". Backend: /api/auswertungen/kanaele/rueckblick?woche=YYYY-Www.
 */
import { FC, ReactNode, useEffect, useMemo, useState } from 'react';
import { Box, MenuItem, TextField, Typography, useMediaQuery, useTheme } from '@mui/material';
import ChartCard from '../../../components/auswertungen/ChartCard';
import SortableTable, { Spalte } from '../../../components/auswertungen/SortableTable';
import { AnfosZelle, euroJeTausend, formatDelta, Hinweis, int, jeTausend, Laden, tEuro } from '../../../components/auswertungen/kennzahlTeile';
import { formatBerlinWallclock } from '../../../helpers/auswertungenData';
import { GLOSSAR, TERMS } from '../../../helpers/kpiDefinitions';
import { formatNumber } from '../../../helpers/numberUtils';
import {
  fetchRueckblick,
  RueckblickAntwort,
  RueckblickFlaeche,
  RueckblickVergleich,
  RueckblickWebinar,
  RueckblickZahlen,
  RueckblickZelle,
} from '../../../helpers/rueckblickData';
import { abweichung, alterSpanne, WELLEN_RANG, wochenText } from './rueckblick';

const datum = (v: string): string => `${v.slice(8, 10)}.${v.slice(5, 7)}.`;
const WOCHENTAG = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
const wochentag = (v: string): string => {
  const [j, m, t] = v.slice(0, 10).split('-').map(Number);
  return WOCHENTAG[new Date(Date.UTC(j, m - 1, t)).getUTCDay()];
};

/** Kleine graue Zeile. */
const Unter: FC<{ children: ReactNode }> = ({ children }) => (
  <Box component="span" sx={{ display: 'block', fontSize: 11, color: 'text.secondary', fontWeight: 400, whiteSpace: 'normal' }}>
    {children}
  </Box>
);

/** Abweichung gegen den Vergleich als Unterzeile („+75 % ggü. Schnitt"); nur bei sicherem Wert. */
const Abweichung: FC<{ jetzt: number | null; vergleich: number | null; grau: boolean }> = ({ jetzt, vergleich, grau }) => {
  const d = abweichung(jetzt, vergleich);
  if (d === null || grau) return null;
  return <Unter>{formatDelta(d)} ggü. Schnitt</Unter>;
};

// ─── Block über den Bestell-Link ──────────────────────────────────────────────────────────────

interface BestellZeile {
  id: string;
  ebene: 'flaeche' | 'versand';
  titel: string;
  unter: string | null;
  alter: number[];
  /** Sortierung der Namensspalte: Flächen in der Reihenfolge der API (FT, Schwestermarken, Partner, Redlink), Versände nach Tag. */
  sortName: string;
  zahlen: RueckblickZahlen;
  vergleich: RueckblickVergleich;
  ohneKontakte: boolean;
  kinder?: BestellZeile[];
}

const MARKE: Record<string, string> = { ft: 'Finanztrends', be: 'Börse-Express', bg: 'Börse-Global', sw: 'Stock-World', tt: 'Trading-Treff', kme: 'KME', dnv: 'DNV' };

function bestellZeilen(f: RueckblickFlaeche[]): BestellZeile[] {
  return f
    .filter((x) => x.versand.length > 0 || x.vergleich.versaende > 0)
    .map((x, i) => ({
      id: x.flaeche,
      ebene: 'flaeche' as const,
      titel: x.label,
      unter: x.versand.length === 0 ? 'kein Versand in dieser Woche' : `${int(x.versand.length)} ${x.versand.length === 1 ? 'Versand' : 'Versände'}`,
      alter: x.versand.map((v) => v.alter_tage),
      sortName: String(i).padStart(2, '0'),
      zahlen: x,
      vergleich: x.vergleich,
      ohneKontakte: false,
      kinder: x.versand.map((v) => ({
        id: `${x.flaeche}/${v.schluessel}`,
        ebene: 'versand' as const,
        titel: `${wochentag(v.send_date)} ${datum(v.send_date)}${v.client_code ? ` · ${MARKE[v.client_code] ?? v.client_code.toUpperCase()}` : ''}`,
        unter: v.name,
        alter: [v.alter_tage],
        sortName: v.send_date,
        zahlen: v,
        vergleich: v.vergleich,
        ohneKontakte: v.ohne_kontakte,
      })),
    }));
}

// ─── Block über die Anmeldung ─────────────────────────────────────────────────────────────────

const WebinarBlock: FC<{ w: RueckblickWebinar; schwelleAnm: number; telefon: boolean; basisN: number }> = ({ w, schwelleAnm, telefon, basisN }) => {
  const a = w.anfos;
  const fest = a.anfos - a.anfos_vorlaeufig;
  const spalten: Spalte<RueckblickZelle>[] = [
    {
      key: 'liste',
      label: TERMS.matrixListe.label,
      tip: TERMS.matrixListe.tip,
      sortValue: (r) => r.label,
      render: (r) => <Box sx={{ fontWeight: 600 }}>{r.label}</Box>,
    },
    {
      key: 'welle',
      label: TERMS.welle.label,
      tip: TERMS.welle.tip,
      sortValue: (r) => WELLEN_RANG[r.welle],
      render: (r) => r.welle_label,
    },
    {
      key: 'kontakte',
      label: TERMS.wellenKontakte.label,
      tip: TERMS.wellenKontakte.tip,
      align: 'right',
      sortValue: (r) => r.kontakte,
      render: (r) => int(r.kontakte),
    },
    {
      key: 'anm',
      label: TERMS.rueckblickAnmeldungen.short,
      tip: TERMS.rueckblickAnmeldungen.tip,
      einheit: 'je 1.000 Kontakte',
      align: 'right',
      unsicher: (r) => (r.anmeldungen < schwelleAnm ? `unter ${schwelleAnm} Anmeldungen — der Wert kann am Zufall hängen` : null),
      sortValue: (r) => r.anmeldungen_je_1000,
      render: (r) => (
        <Box sx={{ lineHeight: 1.25 }}>
          <Box component="span" sx={{ fontWeight: 700 }}>
            {jeTausend(r.anmeldungen_je_1000)}
          </Box>
          <Unter>{int(r.anmeldungen)} Anm.</Unter>
          <Abweichung jetzt={r.anmeldungen_je_1000} vergleich={r.vergleich?.anmeldungen_je_1000 ?? null} grau={r.anmeldungen < schwelleAnm} />
        </Box>
      ),
    },
    {
      key: 'vergleich',
      label: `Schnitt ${basisN} Webinare`,
      tip: `Dieselbe Liste in derselben Welle über die ${basisN} gelaufenen Webinare mit allen drei Wellen vor dieser Woche: Anmeldungen je 1.000 Kontakte. „—": dort nie in dieser Welle verschickt.`,
      einheit: 'je 1.000 Kontakte',
      align: 'right',
      // Grau auch für den Schnitt selbst, wenn er unter der Schwelle liegt — wie „Schnitt der Liste“ in der Matrix (Konsistenzprüfung 29.10.).
      unsicher: (r) => (r.vergleich && r.vergleich.anmeldungen < schwelleAnm ? `Schnitt: unter ${schwelleAnm} Anmeldungen — der Wert kann am Zufall hängen` : null),
      sortValue: (r) => r.vergleich?.anmeldungen_je_1000 ?? null,
      render: (r) =>
        r.vergleich ? (
          <Box sx={{ lineHeight: 1.25 }}>
            {jeTausend(r.vergleich.anmeldungen_je_1000)}
            <Unter>
              {int(r.vergleich.anmeldungen)} Anm. in {r.vergleich.webinare} Webinaren
            </Unter>
          </Box>
        ) : (
          '—'
        ),
    },
  ];
  return (
    <Box sx={{ mb: 2.5 }}>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {w.public_title ?? w.webinar_id}
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1, lineHeight: 1.6 }}>
        {wochentag(w.start_date)} {datum(w.start_date)} · {w.webinar_id} · vor {w.alter_tage} {w.alter_tage === 1 ? 'Tag' : 'Tagen'}
        {w.fehlende_wellen > 0 && ` · ${3 - w.fehlende_wellen} von 3 Wellen`}
        <br />
        <Box component="span" sx={{ color: 'text.primary' }}>
          {TERMS.rueckblickWebinarAnfos.label}:{' '}
          <Box component="span" sx={{ fontWeight: 700 }}>
            {int(a.anfos)}
          </Box>
          {a.anfos_vorlaeufig > 0 && ` (davon ${int(a.anfos_vorlaeufig)} vorläufig)`}
          {a.vergleich.anfos_mittel !== null && (
            <>
              {' '}
              · Schnitt der {a.vergleich.webinare} Webinare davor im selben Alter: {formatNumber(a.vergleich.anfos_mittel, 1)}
              {fest >= 0 && a.vergleich.anfos_mittel > 0 && ` (${formatDelta(abweichung(fest, a.vergleich.anfos_mittel))})`}
            </>
          )}
          {' · '}
          {TERMS.rueckblickWertVorlaeufig.short}: {tEuro(a.wert)}
          {a.vergleich.wert_mittel !== null && ` (Schnitt ${tEuro(a.vergleich.wert_mittel)})`}
        </Box>
      </Typography>
      {w.zellen.length === 0 ? (
        <Hinweis>Zu diesem Webinar ging kein Heat-Up mit Empfängerzahl raus.</Hinweis>
      ) : (
        <SortableTable
          rows={w.zellen}
          columns={telefon ? spalten.filter((c) => ['liste', 'welle', 'anm'].includes(c.key)) : spalten}
          rowKey={(r) => `${r.liste}/${r.welle}`}
          initialSort={{ key: 'welle', dir: 'asc' }}
          maxHeight={null}
          festeErsteSpalte
          eng
        />
      )}
    </Box>
  );
};

// ─── Karte ────────────────────────────────────────────────────────────────────────────────────

const RueckblickKarte: FC = () => {
  const theme = useTheme();
  const telefon = useMediaQuery(theme.breakpoints.down('sm'));
  const [woche, setWoche] = useState<string | null>(null);
  const [stand, setStand] = useState<{ data: RueckblickAntwort | null; fehler: string | null }>({ data: null, fehler: null });
  useEffect(() => {
    let abgebrochen = false;
    fetchRueckblick(woche ?? undefined)
      .then((d) => {
        if (!abgebrochen) setStand({ data: d, fehler: null });
      })
      .catch((e: Error) => {
        if (!abgebrochen) setStand({ data: null, fehler: e.message });
      });
    return () => {
      abgebrochen = true;
    };
  }, [woche]);
  const data = stand.data;
  const laedt = data !== null && woche !== null && data.woche.woche !== woche;
  const g = data?.grau_schwellen;
  const zeilen = useMemo(() => (data ? bestellZeilen(data.bestell_link.flaechen) : []), [data]);

  const unsicherAnfos = (r: BestellZeile) =>
    g && r.zahlen.anfos - r.zahlen.anfos_vorlaeufig < g.anfos ? `unter ${g.anfos} Anfos — der Wert kann am Zufall hängen` : null;
  // Schnitt grau unter der Schwelle — wie „Schnitt der Liste“ in der Matrix (Konsistenzprüfung 29.10.). Die Flächenzeile hat
  // keine eigene Anfo-Zahl (je Versand ein anderes Alter): grau, wenn der Schnitt bei JEDEM ihrer Versände unsicher ist.
  const unsicherVergleich = (r: BestellZeile): string | null => {
    if (!g) return null;
    const unter = (v: RueckblickVergleich) => v.anfos !== null && v.anfos < g.anfos;
    const grau = r.kinder ? r.kinder.length > 0 && r.kinder.every((k) => unter(k.vergleich)) : unter(r.vergleich);
    return grau ? `Schnitt: unter ${g.anfos} Anfos — der Wert kann am Zufall hängen` : null;
  };
  const spalten: Spalte<BestellZeile>[] = [
    {
      key: 'name',
      label: 'Fläche / Versand',
      tip: `${TERMS.werbeflaeche.tip} Darunter je Versand der Woche.`,
      minWidth: 150,
      sortValue: (r) => r.sortName,
      render: (r) => (
        // Fläche: neben dem Aufklapp-Pfeil statt in einer eigenen Zeile darunter (Abnahme 29.10.: der Pfeil stand allein über dem Namen).
        <Box
          sx={{
            lineHeight: 1.3,
            whiteSpace: 'normal',
            ...(r.ebene === 'flaeche'
              ? { display: 'inline-block', verticalAlign: 'middle', maxWidth: 'min(280px, calc(100% - 28px))' }
              : { maxWidth: 280 }),
          }}
        >
          <Box component="span" sx={{ fontWeight: r.ebene === 'flaeche' ? 600 : 400 }}>
            {r.titel}
          </Box>
          {r.unter && <Unter>{r.unter}</Unter>}
        </Box>
      ),
    },
    {
      key: 'alter',
      label: TERMS.rueckblickAlter.label,
      tip: TERMS.rueckblickAlter.tip,
      align: 'right',
      sortValue: (r) => (r.alter.length ? Math.min(...r.alter) : null),
      render: (r) => alterSpanne(r.alter),
    },
    {
      key: 'kontakte',
      label: TERMS.kontakteZustellungen.label,
      tip: 'Zugestellte Mails des Versands (Direkt-Mailing: aktive Empfänger; Redlink: Empfänger der Newsletter-Ausgabe).',
      align: 'right',
      sortValue: (r) => (r.ohneKontakte ? null : r.zahlen.kontakte),
      render: (r) => (r.ohneKontakte ? '—' : int(r.zahlen.kontakte)),
    },
    {
      key: 'anfos',
      label: TERMS.anfos.label,
      tip: 'Anfos mit der Kennung dieses Versands im Bestell-Link, bis heute.',
      align: 'right',
      sortValue: (r) => r.zahlen.anfos,
      render: (r) => <AnfosZelle n={r.zahlen.anfos} vorlaeufig={r.zahlen.anfos_vorlaeufig} />,
    },
    {
      key: 'je1000',
      label: TERMS.rueckblickBisher.short,
      tip: TERMS.rueckblickBisher.tip,
      einheit: 'Anfos je 1.000 Kontakte',
      align: 'right',
      unsicher: unsicherAnfos,
      sortValue: (r) => r.zahlen.anfos_je_1000,
      render: (r) => (
        <Box sx={{ lineHeight: 1.25 }}>
          <Box component="span" sx={{ fontWeight: 700 }}>
            {jeTausend(r.zahlen.anfos_je_1000)}
          </Box>
          <Abweichung jetzt={r.zahlen.anfos_je_1000} vergleich={r.vergleich.anfos_je_1000} grau={unsicherAnfos(r) !== null} />
        </Box>
      ),
    },
    {
      key: 'vergleich',
      label: TERMS.rueckblickVergleich.short,
      tip: TERMS.rueckblickVergleich.tip,
      einheit: 'gleiches Alter',
      align: 'right',
      unsicher: unsicherVergleich,
      sortValue: (r) => r.vergleich.anfos_je_1000,
      render: (r) => (
        <Box sx={{ lineHeight: 1.25 }}>
          {jeTausend(r.vergleich.anfos_je_1000)}
          <Unter>
            {r.vergleich.anfos !== null ? `${int(r.vergleich.anfos)} Anfos · ` : ''}
            {int(r.vergleich.versaende)} Versände
          </Unter>
        </Box>
      ),
    },
    {
      key: 'wert',
      label: TERMS.rueckblickWertVorlaeufig.short,
      tip: TERMS.rueckblickWertVorlaeufig.tip,
      einheit: 'je 1.000 Kontakte',
      align: 'right',
      unsicher: unsicherAnfos,
      sortValue: (r) => r.zahlen.wert_je_1000,
      render: (r) => (
        <Box sx={{ lineHeight: 1.25 }}>
          {euroJeTausend(r.zahlen.wert_je_1000)}
          <Unter>Schnitt {euroJeTausend(r.vergleich.wert_je_1000)}</Unter>
        </Box>
      ),
    },
  ];

  const auswahl = data ? (
    <TextField
      select
      size="small"
      label="Woche"
      value={data.woche.woche}
      onChange={(e) => setWoche(e.target.value)}
      sx={{ minWidth: 210 }}
      disabled={laedt}
    >
      {data.wochen_auswahl.map((w) => (
        <MenuItem key={w.woche} value={w.woche}>
          {wochenText(w.woche, w.von, w.bis)}
        </MenuItem>
      ))}
    </TextField>
  ) : null;

  return (
    <ChartCard
      title="Versände der letzten Woche"
      // Stand = PL-X-Datenstand, bis zu dem eigene Zahl und Vergleich zählen (Audit 30.10.2026 B5) — nicht der Stichtag.
      subtitle={
        data
          ? `${wochenText(data.woche.woche, data.woche.von, data.woche.bis)} · Stand ${
              data.datenstand ? formatBerlinWallclock(data.datenstand) : `${datum(data.as_of)}${data.as_of.slice(0, 4)}`
            }`
          : undefined
      }
      actions={auswahl}
      anker="letzte-woche"
      howToRead={
        <>
          Was die Versände einer vollen Kalenderwoche bisher gebracht haben — die Woche ist jung, es kommen noch Anfos. Deshalb steht
          jeder Wert neben einem Vergleich bei GLEICHEM Alter: Direkt-Mailings und Redlinks gegen den Schnitt derselben Fläche in den 12
          Wochen davor, Heat-Ups gegen dieselbe Liste und Welle der 8 Webinare davor. Oben die Anfos über die Kennung im Bestell-Link,
          unten die Heat-Ups über die Anmeldung — die beiden Blöcke nicht zusammenzählen.
        </>
      }
      terms={[
        { label: TERMS.rueckblickBisher.label, tip: TERMS.rueckblickBisher.tip },
        { label: TERMS.rueckblickVergleich.label, tip: TERMS.rueckblickVergleich.tip },
        { label: TERMS.rueckblickAlter.label, tip: TERMS.rueckblickAlter.tip },
        { label: TERMS.rueckblickWertVorlaeufig.label, tip: TERMS.rueckblickWertVorlaeufig.tip },
        { label: TERMS.rueckblickAnmeldungen.label, tip: TERMS.rueckblickAnmeldungen.tip },
        { label: TERMS.rueckblickWebinarAnfos.label, tip: TERMS.rueckblickWebinarAnfos.tip },
      ]}
      source="PL-X-Bestellungen (Kennung im Bestell-Link, Webinar-Zuordnung), Direkt-Mailing- und Newsletter-Versände mit Empfängern, Webinar-Versände mit Anmeldungen"
      freshnessKey={['plx_orders', 'webinar_send_metrics']}
    >
      {!data ? (
        <Laden fehler={stand.fehler} />
      ) : (
        <Box sx={{ opacity: laedt ? 0.5 : 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
            Über den Bestell-Link: Direkt-Mailings und Redlinks
          </Typography>
          <Box sx={{ fontSize: 13, color: 'text.secondary', mb: 1 }}>
            Vergleich: Versände {datum(data.bestell_link.basis_von)}–{datum(minusTag(data.bestell_link.basis_bis))}
            {data.bestell_link.basis_bis.slice(0, 4)}, jede Anfo dort nur bis zum Alter des Versands dieser Woche
          </Box>
          {zeilen.length === 0 ? (
            <Hinweis>Keine Direkt-Mailings oder Redlinks in dieser Woche und den 12 davor.</Hinweis>
          ) : (
            <SortableTable
              rows={zeilen}
              columns={telefon ? spalten.filter((c) => ['name', 'je1000', 'vergleich'].includes(c.key)) : spalten}
              rowKey={(r) => r.id}
              kinder={(r) => r.kinder}
              initialOffen={zeilen.map((z) => z.id)}
              initialSort={{ key: 'name', dir: 'asc' }}
              maxHeight={null}
              festeErsteSpalte
              eng
            />
          )}
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 3, mb: 0.5 }}>
            Über die Anmeldung: Heat-Ups der Webinare dieser Woche
          </Typography>
          <Box sx={{ fontSize: 13, color: 'text.secondary', mb: 1 }}>
            Vergleich: {data.anmeldung.basis.length} gelaufene Webinare mit allen drei Wellen davor
            {data.anmeldung.basis.length > 0 &&
              ` (${datum(data.anmeldung.basis[data.anmeldung.basis.length - 1].start_date)}–${datum(data.anmeldung.basis[0].start_date)})`}
            . Je Welle nur Anmeldungen: der Versand-Cache kennt keine Bestellzeit, Anfos deshalb nur je Webinar.
          </Box>
          {data.anmeldung.webinare.length === 0 ? (
            <Hinweis>Kein Webinar mit Start in dieser Woche.</Hinweis>
          ) : (
            data.anmeldung.webinare.map((w) => (
              <WebinarBlock key={w.webinar_id} w={w} schwelleAnm={data.grau_schwellen.anmeldungen} telefon={telefon} basisN={data.anmeldung.basis.length} />
            ))
          )}
          <Hinweis>
            Euro = Erwarteter Wert (bei Bestellung, netto) der Anfos bis heute, vorläufig: die Woche ist jung. Vorläufig ergänzte Anfos
            zählen als Anfo, aber in keiner Quote und keinem Euro-Wert. Grau: unter {data.grau_schwellen.anfos} Anfos bzw.{' '}
            {data.grau_schwellen.anmeldungen} Anmeldungen. Redlinks nur mit Finanztrends-Kennung (die Schwestermarken nutzen dieselbe
            Kennung im Freitags-Newsletter) und ab {datum(data.redlink_daten_ab)}
            {data.redlink_daten_ab.slice(0, 4)}. Die Werbeflächen-Karten darüber zählen Versände erst ab 14 Tagen — die Zahlen hier
            sind deshalb nicht mit ihnen abzugleichen. {GLOSSAR.direktMailing} · {GLOSSAR.redlink} · {GLOSSAR.heatUp}.
          </Hinweis>
        </Box>
      )}
    </ChartCard>
  );
};

const minusTag = (v: string): string => {
  const [j, m, t] = v.split('-').map(Number);
  return new Date(Date.UTC(j, m - 1, t - 1)).toISOString().slice(0, 10);
};

export default RueckblickKarte;
